import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import Input from "shared/Input/Input";
import FormItem from "./FormItem";

// @ts-ignore
import { Square3Stack3DIcon, TagIcon, UsersIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocationFormProps } from "data/reduxInterfaces";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { SubmitHandler, useForm } from "react-hook-form";
import { fetchCategorys } from "redux/features/Admin/categorys/categorySlice";
import { fetchAtmospheres } from "redux/features/Admin/atmospheres/atmosphereSlice";
import Select from "shared/Select/Select";
import { Checkbox, Spinner, Typography } from "@material-tailwind/react";
import { z } from "zod";

interface Category {
  id: number;
  designation: any;
}

interface Atmosphere {
  id: number;
  designation: string;
}

const formSchema = z.object({
  title: z.string().min(1, { message: "Le nom du l'espace est obligatoire." }),
  categoryLocationId: z.preprocess(
    (val) => (val === undefined || val === "" ? undefined : Number(val)),
    z.number({
      required_error: "La catégorie est obligatoire.",
      invalid_type_error: "La catégorie doit être un nombre.",
    })
  ),
  surface: z.preprocess(
    (val) => (val === undefined || val === "" ? undefined : Number(val)),
    z.number({
      required_error: "La surface est obligatoire.",
      invalid_type_error: "La surface doit être un nombre.",
    })
  ),
  visitor: z.preprocess(
    (val) => (val === undefined || val === "" ? undefined : Number(val)),
    z.number({
      required_error: "La capacité d'accueil est obligatoire.",
      invalid_type_error: "La capacité d'accueil doit être un nombre.",
    })
  ),
  nbreHour: z.preprocess(
    (val) => (val === undefined || val === "" ? undefined : Number(val)),
    z.number({
      required_error: "Le nombre d'heures est obligatoire.",
      invalid_type_error: "Le nombre d'heures doit être un nombre.",
    })
  ),
  atmosphere: z.array(z.string()).optional(),
});

const LocationProfile: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const dispatch = useAppDispatch();

  const atmospheresStates = useAppSelector((state) => state.featureAtmosphere);
  const [atmosphereDataLoading, setAtmosphereDataLoading] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    watch,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      title: data?.title || "",
      categoryLocationId: (data?.categoryLocationId || "") as any,
      surface: (data?.surface || "") as any,
      visitor: (data?.visitor || "") as any,
      nbreHour: (data?.nbreHour || "") as any,
      atmosphere: data?.atmosphere || [],
    },
    mode: "onSubmit",
  });

  const categorys: Category[] = useAppSelector((state) => state.featureCategory.categories);
  const nbreHours = Array.from({ length: 9 }, (_, index) => ({ value: index + 2 }));

  // Watch form values for real-time updates
  const categoryValue = watch("categoryLocationId");
  const nbreHourValue = watch("nbreHour");

  useEffect(() => {
    dispatch(fetchCategorys());
    dispatch(fetchAtmospheres());
  }, []);

  useEffect(() => {
    if (atmospheresStates.atmospheres) {
      setAtmosphereDataLoading(false);
    }
  }, [atmospheresStates.atmospheres]);

  useEffect(() => {
    if (handleForm.type === "chekLocationFormOne") {
      // Only submit if form has been interacted with or has valid data
      const formValues = getValues();
      if (
        formValues.categoryLocationId ||
        formValues.surface ||
        formValues.visitor ||
        formValues.nbreHour ||
        formValues.title
      ) {
        handleSubmit(onSubmit)();
      }
    }
  }, [handleForm]);

  const handleAtmosphereSelect = (atmosphereDesignation: string, isChecked: boolean) => {
    const updatedAtmospheres = isChecked
      ? [...(getValues("atmosphere") ?? []), atmosphereDesignation]
      : getValues("atmosphere")?.filter((atmosphere: string) => atmosphere !== atmosphereDesignation);

    setValue("atmosphere", updatedAtmospheres as string[]);
    trigger("atmosphere");
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    if (data.nbreHour === undefined) {
      data.nbreHour = 2;
    }
    FormData(data);
  };

  return (
    <Fragment>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="w-full">
          {/* Section Nom de l'annonce */}
          <div>
            <h2 className="text-2xl font-semibold">Titre de votre annonce</h2>
          </div>

          <div className="border-b w-14 border-neutral-200 dark:border-neutral-700"></div>

          <FormItem label="Nom de l'espace *" className="mt-5">
            <div className="mt-1 md:w-1/2">
              <Input
                className={`mt-1.5 ${errors.title?.message ? "border-red-500" : ""}`}
                disabled={isLoading}
                placeholder="Nom de l'espace"
                {...register("title")}
              />
            </div>
            {errors.title?.message && <div className="text-xs text-red-500">{`${errors.title?.message}`}</div>}
          </FormItem>

          <h2 className="mt-8 text-2xl font-semibold">Informations de base</h2>

          <div className="border-b w-14 border-neutral-200 dark:border-neutral-700"></div>

          <div className="my-5 space-y-8">
            <FormItem label="Choisir une catégorie *">
              <div className="md:w-1/2">
                <Select
                  className={`mt-1.5 ${errors.categoryLocationId?.message ? "border-red-500" : ""}`}
                  disabled={isLoading}
                  value={categoryValue ? String(categoryValue) : ""}
                  onChange={(e) => setValue("categoryLocationId", Number(e.target.value))}
                >
                  <option value={""}>Sélectionner une categorie...</option>
                  {categorys.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.designation}
                    </option>
                  ))}
                </Select>
                {errors.categoryLocationId?.message && (
                  <div className="text-xs text-red-500">{`${errors.categoryLocationId?.message}`}</div>
                )}
              </div>
            </FormItem>

            <FormItem label="Superficie (m²) *" className="mt-1.5">
              <div className="md:w-1/2">
                <Input
                  className={`mt-1.5 ${errors.surface?.message ? "border-red-500" : ""}`}
                  placeholder="Unité en m²"
                  type="text"
                  disabled={isLoading}
                  {...register("surface", { valueAsNumber: true })}
                />
                {errors.surface?.message && (
                  <div className="text-xs text-red-500">{`${errors.surface?.message}`}</div>
                )}
              </div>
            </FormItem>

            <FormItem label="Capacité d'accueil *" className="mt-1.5">
              <div className="md:w-1/2">
                <Input
                  className={`mt-1.5 ${errors.visitor?.message ? "border-red-500" : ""}`}
                  placeholder="..."
                  type="text"
                  disabled={isLoading}
                  {...register("visitor", { valueAsNumber: true })}
                />
                {errors.visitor?.message && (
                  <div className="text-xs text-red-500">{`${errors.visitor?.message}`}</div>
                )}
              </div>
            </FormItem>

            <FormItem label="Durée minimale en heures *" className="mt-1.5">
              <div className="md:w-1/2">
                <Select
                  className={`mt-1.5 ${errors.nbreHour?.message ? "border-red-500" : ""}`}
                  disabled={isLoading}
                  value={nbreHourValue ? String(nbreHourValue) : ""}
                  onChange={(e) => setValue("nbreHour", parseInt(e.target.value))}
                >
                  <option value={""}>Sélectionner</option>
                  {nbreHours.map((hour, index) => (
                    <option key={index} value={hour.value}>
                      {hour.value}
                    </option>
                  ))}
                </Select>
                {errors.nbreHour?.message && (
                  <div className="text-xs text-red-500">{`${errors.nbreHour?.message}`}</div>
                )}
              </div>
            </FormItem>
          </div>

          {/* Section Ambiance */}
          <div className="space-y-8">
            <div className="my-5">
              <label className="text-lg font-semibold " htmlFor="">
                Ambiance de votre espace
              </label>
              <div className="border-b w-14 border-neutral-200 dark:border-neutral-700"></div>
              <div
                className={`grid grid-cols-1 mt-6 ${
                  atmospheresStates.atmospheres?.length > 10 ? "sm:grid-cols-2 lg:grid-cols-3" : ""
                }`}
              >
                {atmosphereDataLoading ? (
                  <div className="flex items-center justify-center mt-16">
                    <p>
                      <Spinner color="blue" className="w-12 h-12" />
                    </p>
                  </div>
                ) : (
                  atmospheresStates.atmospheres?.map((atmosphere: Atmosphere) => (
                    <Checkbox
                      key={atmosphere.designation}
                      label={
                        <Typography className="text-dark dark:text-white">{atmosphere.designation}</Typography>
                      }
                      disabled={isLoading}
                      name={atmosphere.designation}
                      checked={!!getValues("atmosphere")?.includes(atmosphere.designation)}
                      onChange={(e) => handleAtmosphereSelect(atmosphere.designation, e.target.checked)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default LocationProfile;
